import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useShowAlert } from 'hooks';
import { makeRequest } from 'services/utils';
import { colors } from 'UI/res';
import { nestTernary, numberFormatter } from 'UI/utils';

import { BULK_ICONS_MAP, ICONS_MAP } from '../constants';

import { useStyles } from './styles';

const CustomIconRenderer = ({ iconKey, classes }) => {
  if (!iconKey || !ICONS_MAP[iconKey]) return null;
  const CustomIcon = ICONS_MAP[iconKey];

  if (BULK_ICONS_MAP[iconKey]) {
    return <CustomIcon classes={{ root: classes.icon }} />;
  }

  return <CustomIcon size={50} fill={colors.oxford} />;
};

const SummaryItem = withStyles({
  root: {
    '&:not(:last-child) > *': {
      borderRight: `1px solid ${colors.lightgrey}`
    }
  }
})(Grid);

const NumberOfItemsByLength = {
  2: 6,
  3: 4,
  5: 4,
  6: 4
};

const minHeight = 60;
const defaultNumberOfItems = 3;
const itemsToMultiline = 5;

/**
 * @param {Object} props
 * @param {Array} [props.data]
 * @param {boolean} [props.fetching]
 * @param {string} [props.lastUpdate]
 * @param {string} [props.url]
 */
const SummaryTotals = ({ data = null, fetching = null, lastUpdate = '', url = '' }) => {
  const { showAlert } = useShowAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [summaryItems, setSummaryItems] = useState([]);

  const manyItems = itemsToMultiline > summaryItems.length;

  const numberOfItemsToShow = NumberOfItemsByLength[summaryItems.length] || defaultNumberOfItems;
  const classes = useStyles({ manyItems });

  useEffect(() => {
    typeof fetching === 'boolean' && setIsLoading(fetching);
  }, [fetching]);

  useEffect(() => {
    setIsLoading(true);
    const shouldExecuteRequest = !data && url;

    shouldExecuteRequest &&
      (async () => {
        const response = await makeRequest({ url, method: 'get' });
        if (response.data && response.success) {
          setSummaryItems(response.data);
        } else {
          showAlert(response.alert);
        }
        setIsLoading(false);
      })();
  }, [data, showAlert, lastUpdate, url]);

  return isLoading ? (
    <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight }}>
      <CircularProgress />
    </Box>
  ) : (
    nestTernary(
      (Array.isArray(data) && data.length > 0) || summaryItems.length > 0,
      <Grid container justify={manyItems ? 'space-between' : 'flex-start'} alignItems="center">
        {(data || summaryItems).map(item => (
          <SummaryItem
            key={`stic-${item.title}`}
            item
            xs={numberOfItemsToShow}
            className={summaryItems.length >= itemsToMultiline ? classes.itemMultiline : null}
          >
            <Box className={classes.summaryItem}>
              {item.icon && <CustomIconRenderer iconKey={item.icon} classes={classes} />}
              <Box>
                <Typography variant="body1" className={classes.summaryText}>
                  {item.title}
                </Typography>
                <Typography variant="h1" className={classes.summaryText}>
                  {item.format === 'none' ? item.total : numberFormatter(item.total, item?.format)}
                </Typography>
              </Box>
            </Box>
          </SummaryItem>
        ))}
      </Grid>,
      <>There is no data with the selected filters</>
    )
  );
};

export default SummaryTotals;
